import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import i18n from "micro-i18n";
import { connect } from "unistore/preact";
import { envConfig } from "config";
import Loader from "components/loader/Loader";
import FinishLoader from "components/loader/FinishLoader";
import Intro from "components/intro/Intro";
import Summary from "components/summary/Summary";
import Question from "components/questions/Question";
import Result from "components/results/Result";
import Card from "components/cards/Card";
import DeckProgress from "components/cards/DeckProgress";
import Recommend from "components/recommend/Recommend";
import SegmentedProgressBar from "components/progress-bar/SegmentedProgressBar";
import { DelayTimer, DispatchEvents, JotType, SubmitState, TextInputLength, WidgetState } from "consts";
import {
  arrayIncludes,
  dispatchEvent,
  evalNatures,
  getDeckState,
  getTextRegEx,
  getTopmostPageUrl,
  isAdFinale,
  isFramed,
  isNullOrUndefOrEmpty,
  isQuestionValidToSubmit,
  onFocusRef,
  toObject,
  widgetCompliance,
  widgetSharing,
} from "utils";
import { actions } from "actions";
import classNames from "classnames";

//FIXME: SOF-617 requires the following imports to be moved to components/widget-components.scss
// due to lazy loading of the widget inside an iframe. Keeping this here for potential fix down the road.
// import 'components/cards/Deck.scss';

const Deck = ({
  adEngageEnabled,
  bootstrap,
  config,
  instanceId,
  articleUrl,
  questionsData = {},
  questionIndex,
  resubmitIds = [],
  servedIds = [],
  submitIds = [],
  results,
  resultIndex,
  finishData,
  complianceState,
  submitState,
  widgetState,
  getResults,
  getQuestions,
  jot,
  setServedIds,
  setShareableLink,
  setSubmitIds,
  setSubmitState,
  onToggleReportModal,
  focusable,
  isCarousel = false,
  isFixed = false,
  complianceUrl,
  onPreviousQuestion,
  onNextButtonCompliance,
  onAfterShowingResults,
}) => {
  const summaryActive = widgetState === WidgetState.Summary && !isNullOrUndefOrEmpty(finishData);

  const {
    questionActive,
    questionLoadingActive,
    hasQuestions,
    hasResults,
    resultActive,
    currentQuestion: currQuestion,
    currentResult: currResult,
    finishActive,
    introActive,
    creSlideActive,
  } = getDeckState({
    widgetState,
    questionsData,
    questionIndex,
    results,
    resultIndex,
  });

  const { id: targetId } = bootstrap;
  const { portalUrl, partnerName, learnUrl, natures = [] } = config;
  const topFocusRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deckHeight, setDeckHeight] = useState(null);
  const [activeCardKey, setActiveCardKey] = useState(null);
  const isDeckHeightSettable = !isFixed && isCarousel;
  const complianceOk = widgetCompliance.complianceStateOk(complianceState);
  const {
    creEnabled,
    pollByEnabled,
    resultsDisabled,
    complianceFirstEnabled,
    expandableComplianceEnabled,
    horizontalOptionsEnabled,
    backButtonEnabled,
    testContentRecAtEndEnabled,
    testNoAutoAdvanceEnabled,
    testQuestionDynamicNextEnabled,
    testShareModalEnabled,
  } = evalNatures(natures);

  //Question Related vars
  const hasCurrentQuestion = hasQuestions && questionIndex < questionsData.questions.length;
  const loadingCurrentQuestion = !hasCurrentQuestion && questionActive;
  const currQuestionId = !hasCurrentQuestion ? null : questionsData.questions[questionIndex].id;

  const isLastQuestion = hasQuestions && questionIndex === questionsData.sessionLength - 1;
  const isResubmission = arrayIncludes(resubmitIds, currQuestionId);
  const submitEnabled = questionActive && (submitState > SubmitState.NotReady || isResubmission);
  const previouslySubmitted = arrayIncludes(submitIds, currQuestionId);
  const hasNextButtonCompliance = !isNullOrUndefOrEmpty(onNextButtonCompliance);
  const { role } = currQuestion || {};
  const questionEventData = hasQuestions && {
    index: questionIndex,
    length: questionsData.sessionLength,
    id: currQuestionId,
    role,
  };

  const isSubmitState = (whichState) => {
    return submitState === whichState;
  };

  // Checking to see if there are AutoSubmittable questions/sessionQuestions
  const isQuestionAutoSubmittable = questionActive && complianceOk && !isSubmitting && isSubmitState(SubmitState.Auto);

  // Results Related vars
  const resultsLength = hasResults ? results.length : 0;
  const { id: currResultId } = toObject(currResult);

  const hasNextResult = hasResults && resultIndex < resultsLength - 1;
  const hasPreviousResult = hasResults && resultIndex > 0;
  const hasPreviousQuestionFromResults = hasResults && resultIndex === 0 && backButtonEnabled;
  const hasOnlyOneResult = hasResults && resultsLength === 1;
  const hasFinish = hasResults && resultIndex === resultsLength - 1;
  //TODO dont use isAdFinale here
  const hasAdFinale = hasResults && isAdFinale(adEngageEnabled, config, results, resultIndex);

  // componentDidUpdate questionActive, isQuestionAutoSubmittable, isSessionQuestionAutoSubmittable, currQuestionId
  useEffect(() => {
    if (questionActive && !loadingCurrentQuestion) {
      //NOTE: AutoSubmit when a question or sessionQuestion
      // is in a submittable state and SubmitState.Auto;
      !!currQuestionId && isQuestionAutoSubmittable && onSubmitQuestion();
      //jot once per question served
      if (!arrayIncludes(servedIds, currQuestionId)) {
        jot({ space: "poll", type: JotType.Served, data: {}, question: currQuestion });
        questionEventData && dispatchEvent(instanceId, this, DispatchEvents.QuestionAsked, false, questionEventData);
        setServedIds([...servedIds, currQuestionId]);
        const nextQuestionIndex = questionIndex + 1;
        const hasNextQuestion = nextQuestionIndex < questionsData.questions.length;
        //get next question, don't call if last question or if we already have next question
        !isLastQuestion && !hasNextQuestion && getQuestions({ questionIndex: questionIndex + 1 });
      }
    }
  }, [questionActive, isQuestionAutoSubmittable, currQuestionId, loadingCurrentQuestion]);

  // componentDidUpdate questionActive, currQuestion, resultActive, currResultId for setShareableLink
  useEffect(() => {
    const shareUrl = testShareModalEnabled ? envConfig.csUserSite : getTopmostPageUrl(articleUrl);
    const shareable =
      isNullOrUndefOrEmpty(shareUrl) && isFramed()
        ? null
        : questionActive && currQuestion
          ? currQuestion
          : resultActive && currResult
            ? currResult
            : null;

    const shareHref =
      shareable &&
      widgetSharing.getShareLink(
        shareUrl,
        targetId,
        shareable,
        testShareModalEnabled ? null : instanceId,
        testShareModalEnabled ? widgetSharing.DEFAULT_SHARE_UTM_PARAMS : undefined,
      );
    const shareText = shareable && widgetSharing.getShareText(targetId, shareable);

    setShareableLink(shareHref && shareText ? { href: shareHref, text: shareText, shareable } : null);

    //componentWillUnmount
    return () => {
      setShareableLink(null);
    };
  }, [questionActive, currQuestion, resultActive, currResultId, articleUrl, testShareModalEnabled]);

  const setTopFocus = () => {
    focusable && onFocusRef(topFocusRef);
  };

  const onSubmitQuestion = () => {
    const submitAction = () => {
      if (!previouslySubmitted) {
        setIsSubmitting(true);
        !previouslySubmitted && setSubmitIds([...submitIds, currQuestionId]);
        jot({ space: "poll", type: JotType.Submit, data: {}, question: currQuestion });
        isLastQuestion && jot({ space: "poll", type: JotType.Finished });
        questionEventData && dispatchEvent(instanceId, this, DispatchEvents.QuestionAnswered, false, questionEventData);

        const submitAnswer = () => {
          complianceOk && getResults({ questionsData, question: currQuestion, currQuestionId });
          setIsSubmitting(false);
        };

        // NOTE: setTimeout is used for a perceived delay when
        // SubmitState.Auto after selecting a RadioQuestionOption.
        // The perceived delay button becomes enabled/active during DelayTimer.SubmitTransitionMS
        setTimeout(() => submitAnswer(), DelayTimer.SubmitTransitionMS);
      }
    };

    //Validate before submitting
    //FIXME: zip lengths are hardcoded in consts for now, but should be definited by the question from the server
    const validationRegEx = getTextRegEx(TextInputLength.ZipMin, TextInputLength.ZipMax);
    //FIXME: isQuestionValidToSubmit is used here to prevent submissions that seem to be caused by Deck component
    // lifecycle which had been double-submitting. Most likely, we should move getResults out of QuestionsList and into a
    // HOC like Widget.js and have only one validation check there.
    if (
      submitEnabled &&
      isQuestionValidToSubmit(currQuestion, questionsData.questions, currQuestionId, validationRegEx)
    ) {
      //NOTE: isSubmitting is necessary to prevent double submission due to component
      // update cycle mixed with setTimeout which is used delay auto submission
      if (complianceOk && !isSubmitting) {
        submitAction();
      } else if (!complianceOk) {
        hasNextButtonCompliance && onNextButtonCompliance();
        setSubmitState(SubmitState.Auto);
      }
    }
  };

  // Note: onActivate is callback when a card is active.
  // onActivate will return height of the child question ref height,
  // due to the carousel using absolute positioning and needing to resize the parent iFrame
  const onActivate = ({ height, cardKey, isResize }) => {
    setDeckHeight(height);
    cardKey !== activeCardKey && !isResize && setTopFocus(); //NOTE: only set top focus when cardKey changes without resize
    setActiveCardKey(cardKey);
  };

  const onRestart = () => {
    onAfterShowingResults();
    // Track user restarting their session
    jot({
      space: "poll",
      type: JotType.EndRestart,
    });
    getQuestions({ reset: true });
  };

  const getDeckProgress = () => {
    const progressType = resultActive ? "PROGRESS_RESULT" : "PROGRESS_QUESTION";

    if (isCarousel) {
      const carouselProgressNum =
        questionActive && hasQuestions ? questionIndex + 1 : resultActive && hasResults ? resultIndex + 1 : 0;
      const carouselTotalNum =
        questionActive && hasQuestions ? questionsData.sessionLength : resultActive && hasResults ? results.length : 0;

      return carouselProgressNum ? (
        <DeckProgress
          progressTypeText={i18n.t(carouselTotalNum === 1 ? progressType : `${progressType}S`)}
          progressNum={carouselProgressNum}
          totalNum={carouselTotalNum}
          showDots={isCarousel}
        />
      ) : (
        <DeckProgress />
      );
    } else {
      const progressNum = finishActive && hasQuestions ? questionsData.sessionLength : questionIndex + 1;
      const totalNum = hasQuestions ? questionsData.sessionLength : 0;
      const progressTotalsText = i18n.t("PROGRESS_OF_TOTAL", { progress: questionIndex, total: totalNum });
      const progressTypeText = i18n.t(totalNum === 1 ? progressType : `${progressType}S`);

      return (
        <SegmentedProgressBar
          id="deck-progress-bar"
          progressNum={progressNum}
          totalNum={totalNum}
          showProgressBar={(questionActive || finishActive) && !isCarousel}
          className={classNames("csw-deck-segmented-progress", { "csw-deck-segmented-progress-fixed": isFixed })}
          progressTitle={`${progressTotalsText} ${progressTypeText}`}
        />
      );
    }
  };

  return (
    <>
      {getDeckProgress()}
      <div className="csw-deck" style={isDeckHeightSettable && deckHeight ? { height: deckHeight } : undefined}>
        <div
          className={classNames({
            "csw-carousel": isCarousel,
            "csw-single": !isCarousel,
          })}
        >
          {(questionActive || resultActive) && (
            <div
              className="csw-question-id"
              data-qid={(questionActive && currQuestionId) || (resultActive && currResultId)}
            />
          )}

          <div className="csw-deck-cards">
            {(questionLoadingActive || loadingCurrentQuestion) && (
              <Card onActivate={onActivate} cardState={{ isActive: true }} cardKey="fetch-question-loader-card">
                <Loader loaderMessage={i18n.t("FETCHING_QUESTIONS")} isAnimated={true} />
              </Card>
            )}

            {introActive && (
              <Card onActivate={onActivate} cardState={{ isActive: true }} cardKey="intro-card">
                <Intro />
              </Card>
            )}
            <>
              {hasQuestions &&
                questionsData.questions.map((q, index) => {
                  const isActive = questionActive && q && q.id === currQuestionId;
                  const isPrevious = questionActive && index >= 0 && questionIndex === index + 1;
                  const isNext = questionActive && index > 0 && questionIndex === index - 1;
                  const isLastQuestionBeforeResults =
                    ((resultActive && resultIndex === 0) || finishActive) && index === questionsData.sessionLength - 1;
                  const showCurrent =
                    (!!targetId && isActive) ||
                    (isCarousel && (isActive || isPrevious || isNext || isLastQuestionBeforeResults));

                  return showCurrent && !!q ? (
                    <Card
                      onActivate={onActivate}
                      cardState={{ isActive, isNext, isPrevious: isPrevious || isLastQuestionBeforeResults }}
                      cardKey={`question-card-${q.id}_${index}`}
                    >
                      <Question
                        key={`question-${q.id}_${index}`}
                        question={q}
                        questionState={{
                          showResultsButton: complianceOk && isLastQuestion && !resultsDisabled,
                          showPreviousButton: backButtonEnabled && questionIndex > 0,
                          submitEnabled,
                          isDisabled: !isActive,
                        }}
                        onSubmit={onSubmitQuestion}
                        onPreviousQuestion={onPreviousQuestion}
                        onActivate={onActivate}
                        topFocusRef={topFocusRef}
                        isSubmitting={isSubmitting}
                        complianceUrl={complianceUrl}
                        partnerName={partnerName}
                        hasNextButtonCompliance={hasNextButtonCompliance}
                        focusable={focusable}
                        expandable={complianceFirstEnabled && expandableComplianceEnabled}
                        onToggleReportModal={onToggleReportModal}
                        pollByEnabled={pollByEnabled}
                        horizontalOptionsEnabled={horizontalOptionsEnabled}
                        testNoAutoAdvanceEnabled={testNoAutoAdvanceEnabled}
                        testQuestionDynamicNextEnabled={testQuestionDynamicNextEnabled}
                        natures={natures}
                      />
                    </Card>
                  ) : null;
                })}

              {finishActive && (
                <Card onActivate={onActivate} cardState={{ isActive: true }} cardKey="finish-loader-card">
                  <FinishLoader />
                </Card>
              )}

              {hasResults &&
                results.map((r, index) => {
                  const isActive = resultActive && r && r.id === currResultId;
                  const isPrevious = index >= 0 && resultIndex === index + 1;
                  const isNext = (isLastQuestion || resultActive) && index > 0 && resultIndex === index - 1;
                  const showCurrent = isActive || (isCarousel && (isActive || isPrevious || isNext));

                  return showCurrent && !!r ? (
                    <Card
                      cardState={{ isActive, isNext, isPrevious }}
                      onActivate={onActivate}
                      cardKey={`result-${r.id}_${index}`}
                    >
                      <Result
                        key={`result-${r.id}_${index}`}
                        result={r}
                        resultState={{
                          isHidden: !resultActive,
                          hasOnlyOne: hasOnlyOneResult,
                          hasNext: hasNextResult,
                          hasPrevious: hasPreviousResult,
                          hasPreviousQuestion: hasPreviousQuestionFromResults,
                          hasFinish,
                          hasAdFinale,
                          isActive,
                        }}
                        portalUrl={portalUrl}
                        learnUrl={learnUrl}
                        partnerName={partnerName}
                        topFocusRef={topFocusRef}
                        onRestart={onRestart}
                        pollByEnabled={pollByEnabled}
                        creEnabled={creEnabled}
                        testContentRecAtEndEnabled={testContentRecAtEndEnabled}
                        isCarousel={isCarousel}
                      />
                    </Card>
                  ) : null;
                })}
            </>

            {testContentRecAtEndEnabled && ((isCarousel && hasResults && resultActive) || creSlideActive) && (
              <Card
                onActivate={onActivate}
                cardState={{
                  isActive: creSlideActive,
                  isNext: resultActive && hasFinish && !creSlideActive,
                }}
                cardKey="recommendation-card"
              >
                <Recommend
                  onRestart={onRestart}
                  creEnabled={creEnabled}
                  testContentRecAtEndEnabled={testContentRecAtEndEnabled}
                  isCarousel={isCarousel}
                />
              </Card>
            )}

            {summaryActive && (
              <Card onActivate={onActivate} cardState={{ isActive: true }} cardKey="summary-card">
                <Summary focusable={focusable} resultsDisabled={resultsDisabled} />
              </Card>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

Deck.propTypes /* remove-proptypes */ = {
  adEngageEnabled: PropTypes.bool,
  bootstrap: PropTypes.object.isRequired,
  config: PropTypes.object.isRequired,
  instanceId: PropTypes.string,
  articleUrl: PropTypes.string,
  questionsData: PropTypes.object,
  questionIndex: PropTypes.number,
  resubmitIds: PropTypes.array,
  servedIds: PropTypes.array,
  submitIds: PropTypes.array,
  results: PropTypes.array.isRequired,
  resultIndex: PropTypes.number.isRequired,
  finishData: PropTypes.object,
  complianceState: PropTypes.string,
  submitState: PropTypes.number,
  widgetState: PropTypes.number,
  getResults: PropTypes.func.isRequired,
  getQuestions: PropTypes.func.isRequired,
  jot: PropTypes.func.isRequired,
  setServedIds: PropTypes.func.isRequired,
  setShareableLink: PropTypes.func.isRequired,
  setSubmitIds: PropTypes.func.isRequired,
  setSubmitState: PropTypes.func.isRequired,
  onToggleReportModal: PropTypes.func.isRequired,
  focusable: PropTypes.bool,
  isCarousel: PropTypes.bool,
  isFixed: PropTypes.bool,
  complianceUrl: PropTypes.string,
  onPreviousQuestion: PropTypes.func,
  onNextButtonCompliance: PropTypes.func,
  onAfterShowingResults: PropTypes.func.isRequired,
};

export default connect(
  `adEngageEnabled,bootstrap,config,instanceId,articleUrl,
  questionsData,questionIndex,resubmitIds,servedIds,submitIds,
  results,resultIndex,finishData,
  complianceState,submitState,widgetState`,
  actions,
)(Deck);
